(() => {
  'use strict';

  if (window.MontkushYotpoReviews) {
    window.MontkushYotpoReviews.initAll(document);
    return;
  }

  const REQUEST_SIZE = 50;
  const MAX_PAGES = 8;
  const VISIBLE_PAGE_SIZE = 10;
  const feedCache = new Map();

  const text = (value) => value == null ? '' : String(value);
  const number = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(maximum, value));
  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

  const create = (tagName, className, content) => {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (content != null) node.textContent = String(content);
    return node;
  };

  const replaceChildren = (node, children) => node && node.replaceChildren(...children);
  const setHidden = (node, hidden) => { if (node) node.hidden = Boolean(hidden); };

  const safeUrl = (value) => {
    if (!value || typeof value !== 'string') return '';
    try {
      const url = new URL(value, window.location.origin);
      return /^(https?:)$/.test(url.protocol) ? url.href : '';
    } catch (_) {
      return '';
    }
  };

  const formatDate = (value) => {
    const date = new Date(value || '');
    return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    }).format(date);
  };

  const dateValue = (review) => {
    const value = review.created_at || review.created_at_date || review.date || review.updated_at;
    const stamp = new Date(value || '').getTime();
    return Number.isNaN(stamp) ? 0 : stamp;
  };

  const reviewImages = (review) => (Array.isArray(review.images_data) ? review.images_data : [])
    .map((image) => {
      if (typeof image === 'string') return safeUrl(image);
      if (!image || typeof image !== 'object') return '';
      return safeUrl(image.original_url || image.url || image.full_url || image.big || image.thumb_url || image.thumbnail_url);
    })
    .filter(Boolean);

  const normaliseReview = (raw) => ({
    id: text(raw?.id || raw?.review_id),
    score: clamp(number(raw?.score || raw?.rating), 0, 5),
    title: text(raw?.title).trim(),
    content: text(raw?.content || raw?.review_content || raw?.body).trim(),
    reviewer: text(raw?.user?.display_name || raw?.user?.name || raw?.display_name || raw?.reviewer_name).trim(),
    createdAt: raw?.created_at || raw?.created_at_date || raw?.date || raw?.updated_at || '',
    date: formatDate(raw?.created_at || raw?.created_at_date || raw?.date || raw?.updated_at),
    verified: Boolean(raw?.verified_buyer || raw?.verified || raw?.is_verified_buyer),
    incentivized: Boolean(raw?.incentivized_review || raw?.incentivized || raw?.is_incentivized_review),
    media: reviewImages(raw),
    reply: text(raw?.merchant_response || raw?.merchant_reply || raw?.reply || raw?.response?.content || raw?.response?.body).trim(),
    helpful: Number.isFinite(Number(raw?.votes_up ?? raw?.helpful_count ?? raw?.votes_up_count))
      ? Math.max(0, Number(raw?.votes_up ?? raw?.helpful_count ?? raw?.votes_up_count))
      : null,
    raw
  });

  const endpointFor = (storeId, productId, page, perPage = REQUEST_SIZE) => {
    const safePage = Math.max(1, number(page, 1));
    const safeSize = clamp(number(perPage, REQUEST_SIZE), 1, 150);
    return `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(storeId)}/products/${encodeURIComponent(productId)}/reviews.json?per_page=${safeSize}&page=${safePage}`;
  };

  const feedFor = (storeId, productId) => {
    const key = `${storeId}:${productId}`;
    if (!feedCache.has(key)) {
      feedCache.set(key, {
        storeId, productId, page: 0, total: null, bottomline: null,
        reviews: [], pending: null, exhausted: false, error: ''
      });
    }
    return feedCache.get(key);
  };

  const loadPage = (feed) => {
    if (feed.pending || feed.exhausted || feed.page >= MAX_PAGES) return feed.pending || Promise.resolve(feed);
    feed.pending = fetch(endpointFor(feed.storeId, feed.productId, feed.page + 1), {
      headers: { Accept: 'application/json' }, credentials: 'omit'
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`The reviews service returned ${response.status}.`);
        const body = await response.json();
        if (number(body?.status?.code, 200) >= 400) throw new Error('The reviews service could not return published reviews.');
        const payload = body?.response;
        if (!payload || !Array.isArray(payload.reviews)) throw new Error('The reviews service returned an unexpected response.');
        const incoming = payload.reviews.map(normaliseReview);
        const known = new Set(feed.reviews.map((review) => review.id).filter(Boolean));
        incoming.forEach((review) => {
          if (!review.id || !known.has(review.id)) {
            feed.reviews.push(review);
            if (review.id) known.add(review.id);
          }
        });
        feed.page += 1;
        feed.bottomline = payload.bottomline || feed.bottomline;
        feed.total = Number.isFinite(Number(payload?.pagination?.total)) ? Number(payload.pagination.total) : feed.total;
        const returnedSize = number(payload?.pagination?.per_page, REQUEST_SIZE);
        feed.exhausted = feed.page >= MAX_PAGES || incoming.length === 0 || incoming.length < returnedSize || (feed.total !== null && feed.reviews.length >= feed.total);
        feed.error = '';
        return feed;
      })
      .catch((error) => {
        feed.error = error instanceof Error ? error.message : 'Reviews could not be loaded.';
        feed.exhausted = true;
        return feed;
      })
      .finally(() => { feed.pending = null; });
    return feed.pending;
  };

  const stars = (score) => {
    const value = clamp(number(score), 0, 5);
    const root = create('span', 'mk-yotpo-reviews__stars');
    root.setAttribute('role', 'img');
    root.setAttribute('aria-label', `${value.toFixed(1)} out of 5 stars`);
    const base = create('span', 'mk-yotpo-reviews__stars-base', '★★★★★');
    base.setAttribute('aria-hidden', 'true');
    const fill = create('span', 'mk-yotpo-reviews__stars-fill', '★★★★★');
    fill.setAttribute('aria-hidden', 'true');
    fill.style.width = `${(value / 5) * 100}%`;
    root.append(base, fill);
    return root;
  };

  const skeletons = () => Array.from({ length: 3 }, () => create('div', 'mk-yotpo-reviews__skeleton'));

  const reviewCard = (review) => {
    const card = create('article', 'mk-yotpo-reviews__card');
    const top = create('div', 'mk-yotpo-reviews__top');
    top.append(stars(review.score), create('span', 'mk-yotpo-reviews__score', `${review.score.toFixed(1)} / 5`));
    if (review.date) {
      const date = create('time', 'mk-yotpo-reviews__date', review.date);
      date.dateTime = review.createdAt;
      top.append(date);
    }
    card.append(top);
    if (review.title) card.append(create('h3', 'mk-yotpo-reviews__title', review.title));
    if (review.content) card.append(create('p', 'mk-yotpo-reviews__content', review.content));

    const meta = create('div', 'mk-yotpo-reviews__meta');
    if (review.reviewer) meta.append(create('span', '', review.reviewer));
    if (review.verified) meta.append(create('span', 'mk-yotpo-reviews__badge', 'Verified buyer'));
    if (review.incentivized) meta.append(create('span', 'mk-yotpo-reviews__badge mk-yotpo-reviews__badge--disclosure', 'Incentivized review'));
    if (review.helpful !== null) meta.append(create('span', 'mk-yotpo-reviews__helpful', `${plural(review.helpful, 'person', 'people')} found this helpful`));
    if (meta.childNodes.length) card.append(meta);

    if (review.media.length) {
      const media = create('div', 'mk-yotpo-reviews__media');
      review.media.forEach((url, index) => {
        const image = document.createElement('img');
        image.src = url;
        image.loading = 'lazy';
        image.alt = `Customer review photo ${index + 1}`;
        media.append(image);
      });
      card.append(media);
    }

    if (review.reply) {
      const reply = create('aside', 'mk-yotpo-reviews__reply');
      reply.append(create('strong', '', 'MONTKUSH reply'), create('span', '', review.reply));
      card.append(reply);
    }
    return card;
  };

  const init = (root) => {
    if (!root || root.dataset.mkYotpoInitialised === 'true') return;
    root.dataset.mkYotpoInitialised = 'true';

    const storeId = text(root.dataset.yotpoStore).trim();
    const productId = text(root.dataset.productId).trim();
    const form = root.querySelector('[data-mk-yotpo-filters]');
    const results = root.querySelector('[data-mk-yotpo-results]');
    const status = root.querySelector('[data-mk-yotpo-status]');
    const errorBox = root.querySelector('[data-mk-yotpo-error]');
    const errorCopy = root.querySelector('[data-mk-yotpo-error-copy]');
    const empty = root.querySelector('[data-mk-yotpo-empty]');
    const loadMore = root.querySelector('[data-mk-yotpo-load-more]');
    const clear = root.querySelector('[data-mk-yotpo-clear]');
    const summary = root.querySelector('[data-mk-yotpo-summary]');
    const ratingMenu = root.querySelector('[data-mk-yotpo-rating-menu]');
    const ratingSummary = root.querySelector('[data-mk-yotpo-rating-summary]');
    const ratingCounts = [...root.querySelectorAll('[data-mk-yotpo-rating-count]')];
    if (!storeId || !productId || !form || !results) return;

    const feed = feedFor(storeId, productId);
    let visible = VISIBLE_PAGE_SIZE;
    let loading = false;

    const controls = () => ({
      query: text(form.querySelector('[name="query"]')?.value).trim().toLocaleLowerCase(),
      ratings: [...form.querySelectorAll('input[name="mk-yotpo-rating"]:checked')]
        .map((input) => number(input.value)).filter((rating) => rating >= 1 && rating <= 5),
      media: Boolean(form.querySelector('[name="media"]')?.checked),
      sort: form.querySelector('[name="sort"]')?.value || 'newest'
    });

    const filtered = () => {
      const filter = controls();
      const results = feed.reviews.filter((review) => {
        if (filter.ratings.length && !filter.ratings.includes(Math.round(review.score))) return false;
        if (filter.media && !review.media.length) return false;
        if (filter.query && !`${review.title} ${review.content} ${review.reviewer}`.toLocaleLowerCase().includes(filter.query)) return false;
        return true;
      });
      return results.sort((a, b) => {
        if (filter.sort === 'highest') return b.score - a.score || dateValue(b.raw) - dateValue(a.raw);
        if (filter.sort === 'lowest') return a.score - b.score || dateValue(b.raw) - dateValue(a.raw);
        if (filter.sort === 'helpful') return (b.helpful || 0) - (a.helpful || 0) || dateValue(b.raw) - dateValue(a.raw);
        return dateValue(b.raw) - dateValue(a.raw);
      });
    };

    const updateRatingSummary = () => {
      const ratings = controls().ratings;
      if (!ratingSummary) return;
      ratingSummary.textContent = !ratings.length ? 'All ratings' : ratings.length === 1 ? `${ratings[0]} ${ratings[0] === 1 ? 'star' : 'stars'}` : `${ratings.length} ratings`;
    };

    const updateCounts = () => {
      const counts = new Map([[1, 0], [2, 0], [3, 0], [4, 0], [5, 0]]);
      feed.reviews.forEach((review) => {
        const score = Math.round(review.score);
        if (counts.has(score)) counts.set(score, counts.get(score) + 1);
      });
      ratingCounts.forEach((node) => {
        const rating = number(node.dataset.rating);
        const count = counts.get(rating) || 0;
        node.textContent = `(${count})`;
        const label = node.closest('.mk-yotpo-reviews__rating-option')?.querySelector('[data-mk-yotpo-rating-label]');
        if (label) label.textContent = `${rating} ${rating === 1 ? 'star' : 'stars'}, ${plural(count, 'review', 'reviews')}`;
      });
    };

    const updateHeader = () => {
      const total = number(feed.bottomline?.total_review, feed.total || 0);
      const average = number(feed.bottomline?.average_score);
      if (summary && total > 0 && average > 0) {
        summary.hidden = false;
        replaceChildren(summary, [stars(average), create('span', '', `${average.toFixed(1)} / 5 · ${plural(total, 'published review', 'published reviews')}`)]);
      }
    };

    const render = () => {
      const matching = filtered();
      updateCounts();
      updateHeader();
      replaceChildren(results, matching.slice(0, visible).map(reviewCard));
      results.setAttribute('aria-busy', loading ? 'true' : 'false');
      const hasError = Boolean(feed.error);
      setHidden(errorBox, !hasError || feed.reviews.length > 0);
      if (hasError && errorCopy) errorCopy.textContent = feed.error;
      setHidden(empty, loading || hasError || matching.length > 0 || feed.reviews.length > 0);
      setHidden(results, hasError && feed.reviews.length === 0);
      if (status) {
        status.textContent = loading
          ? 'Loading published reviews…'
          : matching.length
            ? `${plural(matching.length, 'published review', 'published reviews')} match the current filters.`
            : feed.reviews.length
              ? 'No loaded reviews match the current filters.'
              : hasError
                ? 'Reviews could not be loaded right now.'
                : 'No published reviews are available yet.';
      }
      const remainingLocal = matching.length > visible;
      const mayFetch = !feed.exhausted && feed.page < MAX_PAGES;
      setHidden(loadMore, !(remainingLocal || mayFetch));
      if (loadMore) {
        loadMore.disabled = loading;
        loadMore.textContent = loading ? 'Loading reviews…' : remainingLocal ? 'Load more reviews' : 'Load more from Yotpo';
      }
    };

    const initial = async () => {
      loading = true;
      if (status) status.textContent = 'Loading published reviews…';
      replaceChildren(results, skeletons());
      results.setAttribute('aria-busy', 'true');
      await loadPage(feed);
      loading = false;
      render();
    };

    const more = async () => {
      const matching = filtered();
      if (matching.length > visible) {
        visible += VISIBLE_PAGE_SIZE;
        render();
        return;
      }
      if (loading || feed.exhausted || feed.page >= MAX_PAGES) return;
      loading = true;
      render();
      await loadPage(feed);
      visible += VISIBLE_PAGE_SIZE;
      loading = false;
      render();
    };

    form.addEventListener('input', () => { visible = VISIBLE_PAGE_SIZE; render(); });
    form.addEventListener('change', () => { visible = VISIBLE_PAGE_SIZE; updateRatingSummary(); render(); });
    clear?.addEventListener('click', () => {
      form.reset();
      visible = VISIBLE_PAGE_SIZE;
      ratingMenu?.removeAttribute('open');
      updateRatingSummary();
      render();
      form.querySelector('[name="query"]')?.focus();
    });
    ratingMenu?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        ratingMenu.removeAttribute('open');
        ratingMenu.querySelector('summary')?.focus();
      }
    });
    loadMore?.addEventListener('click', more);
    updateRatingSummary();
    initial();
  };

  const observe = (root) => {
    if (!('IntersectionObserver' in window)) {
      init(root);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      init(root);
    }, { rootMargin: '300px 0px' });
    observer.observe(root);
  };

  const initAll = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-mk-yotpo-reviews]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-mk-yotpo-reviews]'));
    roots.forEach(observe);
  };

  window.MontkushYotpoReviews = { initAll, endpointFor };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
